import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { Project, ProjectInput, projectsApi } from '../api/projects';

type ProjectContextValue = {
  projects: Project[];
  ready: boolean;
  selectedProject: Project | null;
  selectProject: (id: string | null) => void;
  /** Vrai pour toutes les factures quand aucun projet n'est choisi. */
  includesInvoice: (invoiceId?: string | null) => boolean;
  createProject: (input: ProjectInput) => Promise<Project>;
  updateProject: (id: string, input: ProjectInput) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
};

const ProjectContext = createContext<ProjectContextValue | null>(null);

const storageKey = (uid: string) => `stars-project:${uid}`;

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!firebaseUser) {
      setProjects([]);
      setSelectedId(null);
      setReady(false);
      return;
    }
    let cancelled = false;
    setSelectedId(localStorage.getItem(storageKey(firebaseUser.uid)));
    setReady(false);
    (async () => {
      try {
        const list = await projectsApi.list();
        if (!cancelled) setProjects(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedId) ?? null,
    [projects, selectedId],
  );

  useEffect(() => {
    if (!ready || !firebaseUser || !selectedId) return;
    if (!projects.some((project) => project.id === selectedId)) {
      setSelectedId(null);
      localStorage.removeItem(storageKey(firebaseUser.uid));
    }
  }, [ready, firebaseUser, projects, selectedId]);

  const selectProject = useCallback((id: string | null) => {
    setSelectedId(id);
    if (!firebaseUser) return;
    const key = storageKey(firebaseUser.uid);
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  }, [firebaseUser]);

  const includesInvoice = useCallback((invoiceId?: string | null) => {
    if (!selectedProject) return true;
    if (!invoiceId) return false;
    return selectedProject.invoiceIds.includes(invoiceId);
  }, [selectedProject]);

  const createProject = useCallback(async (input: ProjectInput) => {
    const created = await projectsApi.create(input);
    setProjects((prev) => [created, ...prev.filter((project) => project.id !== created.id)]);
    selectProject(created.id);
    return created;
  }, [selectProject]);

  const updateProject = useCallback(async (id: string, input: ProjectInput) => {
    const updated = await projectsApi.update(id, input);
    setProjects((prev) => prev.map((project) => (project.id === id ? updated : project)));
    return updated;
  }, []);

  const deleteProject = useCallback(async (id: string) => {
    await projectsApi.remove(id);
    setProjects((prev) => prev.filter((project) => project.id !== id));
    setSelectedId((current) => {
      if (current !== id) return current;
      if (firebaseUser) localStorage.removeItem(storageKey(firebaseUser.uid));
      return null;
    });
  }, [firebaseUser]);

  const value = useMemo<ProjectContextValue>(() => ({
    projects,
    ready,
    selectedProject,
    selectProject,
    includesInvoice,
    createProject,
    updateProject,
    deleteProject,
  }), [projects, ready, selectedProject, selectProject, includesInvoice, createProject, updateProject, deleteProject]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export function useProjects(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjects must be used within ProjectProvider');
  return ctx;
}
